import { Button } from "@/components/ui/button";
import { ContentContainer } from "@/components/layout/content-container";
import { PageLayout } from "@/components/layout/page-layout";
import { PageTitle } from "@/components/layout/page-title";
import { useGenerateScanMutation } from "@/services/conciliation/dtef-validation";
import { downloadExcel } from "@/utils/download-excel";
import { Download, FileSpreadsheet, RefreshCw } from "lucide-react";
import { useState } from "react";
import { Transaction } from "./components/columns";
import { DtefTransactionsTable } from "./components/dtef-transactions-table";
import { FileUpload } from "./components/file-upload";
import { MESSAGES } from "./utils/messages";
import { toast } from "components/ui/use-toast";
import { ValidationHistoryTable } from "./components/validation-history-table";

interface ScanData {
  scan: Transaction[];
  scanned_by: string;
  scanned_at: string;
  excelBase64: string;
}

interface ApiResponse {
  data: ScanData[];
  meta: {
    total: number;
    lastPage: number;
    currentPage: number;
    perPage: number;
    prev: null | number;
    next: null | number;
  };
}

const DtefValidation = () => {
  const [generateScan, { isLoading }] = useGenerateScanMutation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [scanData, setScanData] = useState<ScanData | null>(null);

  const handleFileSelect = (file: File | null) => {
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      const response = (await generateScan({
        file: selectedFile,
      }).unwrap()) as ApiResponse;

      if (response.data && response.data.length > 0) {
        setScanData(response.data[0]);
      }
      toast(MESSAGES.CREATE.SUCCESS);
    } catch (error) {
      console.error("Upload failed:", error);
      toast(MESSAGES.CREATE.ERROR);
    }
  };

  const handleClearAll = () => {
    setScanData(null);
    setSelectedFile(null);
  };

  const handleDownloadExcel = () => {
    if (scanData?.excelBase64) {
      downloadExcel(scanData.excelBase64, `Validação_DTEF.xlsx`);
    }
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement("a");
    link.href = "/dtef_template.xlsx";
    link.download = "dtef-template.xlsx";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <PageLayout>
      <PageTitle
        title="Validação D-TEF"
        description="Carregue arquivos Excel contendo transações DTEF pendentes para validação de registros no VAR"
      />
      <ContentContainer>
        <FileUpload
          selectedFile={selectedFile}
          onFileSelect={handleFileSelect}
          onUpload={handleUpload}
          isLoading={isLoading}
        />

        <div className="mt-4">
          <DtefTransactionsTable data={scanData} />
        </div>

        <div className="flex flex-wrap gap-3 justify-between w-full mt-8">
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              onClick={handleClearAll}
              disabled={isLoading}
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Limpar Tudo
            </Button>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              onClick={handleDownloadTemplate}
              disabled={isLoading}
            >
              <Download className="mr-2 h-4 w-4" /> Baixar Template
            </Button>
            <Button
              className="bg-[#F6D040] text-[#613F4F] hover:bg-[#F6D040]/90"
              onClick={handleDownloadExcel}
              disabled={!scanData || isLoading}
            >
              <FileSpreadsheet className="mr-2 h-4 w-4" /> Download Excel
            </Button>
          </div>
        </div>

        <ValidationHistoryTable />
      </ContentContainer>
    </PageLayout>
  );
};

export default DtefValidation;
